import { useState, useEffect, useCallback } from 'react';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import InputAdornment from '@mui/material/InputAdornment';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { ConfirmDialog } from 'src/components/custom-dialog';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
// sections
import SpecialClosedDateForm from '../special-closed-date-form';

// ----------------------------------------------------------------------

export default function BusinessHoursListView() {
  const { enqueueSnackbar } = useSnackbar();

  const [businessHours, setBusinessHours] = useState([]);
  const [closedDates, setClosedDates] = useState([]);
  const [editingDay, setEditingDay] = useState(null);
  const [toDeleteId, setToDeleteId] = useState(null);

  const closedDateDialog = useBoolean();
  const confirmDelete = useBoolean();

  const fetchBusinessHours = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.businessHours.list);
      setBusinessHours(res.data);
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
      enqueueSnackbar('Erro ao carregar horários de funcionamento', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const fetchClosedDates = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.businessHours.closedDates);
      setClosedDates(res.data);
    } catch (error) {
      console.error('Erro ao carregar datas especiais:', error);
    }
  }, []);

  useEffect(() => {
    fetchBusinessHours();
    fetchClosedDates();
  }, [fetchBusinessHours, fetchClosedDates]);

  const handleToggleOpen = async (day) => {
    try {
      const updatedDay = { ...day, isOpen: !day.isOpen };
      await axios.put(API_ENDPOINTS.businessHours.update, updatedDay);
      enqueueSnackbar('Horário atualizado com sucesso!', { variant: 'success' });
      fetchBusinessHours();
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar horário', { variant: 'error' });
    }
  };

  const handleTimeChange = (dayOfWeek, field, value) => {
    setBusinessHours(prev =>
      prev.map(h =>
        h.dayOfWeek === dayOfWeek ? { ...h, [field]: value } : h
      )
    );
    setEditingDay(dayOfWeek);
  };

  const handleSaveDay = async (day) => {
    try {
      await axios.put(API_ENDPOINTS.businessHours.update, {
        dayOfWeek: day.dayOfWeek,
        isOpen: day.isOpen,
        openTime: day.openTime || null,
        closeTime: day.closeTime || null,
        openTime2: day.openTime2 || null,
        closeTime2: day.closeTime2 || null,
      });
      enqueueSnackbar('Horário guardado com sucesso!', { variant: 'success' });
      setEditingDay(null);
      fetchBusinessHours();
    } catch (error) {
      enqueueSnackbar('Erro ao guardar horário', { variant: 'error' });
    }
  };

  const handleDeleteClosedDate = async () => {
    try {
      await axios.delete(`${API_ENDPOINTS.businessHours.deleteClosedDate}${toDeleteId}`);
      enqueueSnackbar('Data especial eliminada com sucesso!', { variant: 'success' });
      confirmDelete.onFalse();
      fetchClosedDates();
    } catch (error) {
      enqueueSnackbar('Erro ao eliminar data especial', { variant: 'error' });
    }
  };

  const handleClosedDateCreated = () => {
    closedDateDialog.onFalse();
    fetchClosedDates();
  };

  return (
    <Container maxWidth="lg">
      <CustomBreadcrumbs
        heading="Horários de Funcionamento"
        links={[
          { name: 'Dashboard', href: '/dashboard' },
          { name: 'Horários de Funcionamento' },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      {/* Horários Semanais */}
      <Card sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 3 }}>
          <Typography variant="h6">Horário Semanal</Typography>
        </Stack>

        <Divider />

        <TableContainer sx={{ overflow: 'unset' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>Dia</TableCell>
                <TableCell align="center">Aberto</TableCell>
                <TableCell align="center">Abertura</TableCell>
                <TableCell align="center">Fecho</TableCell>
                <TableCell align="center">Abertura 2</TableCell>
                <TableCell align="center">Fecho 2</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {businessHours.map((day) => (
                <TableRow key={day.dayOfWeek} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{day.dayName}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={day.isOpen}
                      onChange={() => handleToggleOpen(day)}
                      color="success"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="time"
                      value={day.openTime || ''}
                      onChange={(e) => handleTimeChange(day.dayOfWeek, 'openTime', e.target.value)}
                      disabled={!day.isOpen}
                      sx={{ width: 130 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon="mdi:clock-outline" width={18} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="time"
                      value={day.closeTime || ''}
                      onChange={(e) => handleTimeChange(day.dayOfWeek, 'closeTime', e.target.value)}
                      disabled={!day.isOpen}
                      sx={{ width: 130 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="time"
                      value={day.openTime2 || ''}
                      onChange={(e) => handleTimeChange(day.dayOfWeek, 'openTime2', e.target.value)}
                      disabled={!day.isOpen}
                      sx={{ width: 130 }}
                      placeholder="Opcional"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="time"
                      value={day.closeTime2 || ''}
                      onChange={(e) => handleTimeChange(day.dayOfWeek, 'closeTime2', e.target.value)}
                      disabled={!day.isOpen}
                      sx={{ width: 130 }}
                      placeholder="Opcional"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {editingDay === day.dayOfWeek && (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => handleSaveDay(day)}
                        startIcon={<Iconify icon="mdi:content-save" />}
                      >
                        Guardar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Datas Especiais de Encerramento */}
      <Card>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 3 }}>
          <Typography variant="h6">Datas Especiais de Encerramento</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={closedDateDialog.onTrue}
          >
            Adicionar Data
          </Button>
        </Stack>

        <Divider />

        <TableContainer sx={{ overflow: 'unset' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell align="center">Recorrente Anual</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {closedDates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box sx={{ py: 5 }}>
                      <Iconify icon="mdi:calendar-blank" width={48} sx={{ color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma data especial registada
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                closedDates.map((closedDate) => (
                  <TableRow key={closedDate.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {new Date(closedDate.date).toLocaleDateString('pt-PT', {
                          day: '2-digit',
                          month: 'long',
                          year: closedDate.isRecurringYearly ? undefined : 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>{closedDate.reason || '-'}</TableCell>
                    <TableCell align="center">
                      {closedDate.isRecurringYearly ? (
                        <Iconify icon="mdi:check-circle" color="success.main" width={24} />
                      ) : (
                        <Iconify icon="mdi:close-circle" color="text.disabled" width={24} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => {
                          setToDeleteId(closedDate.id);
                          confirmDelete.onTrue();
                        }}
                      >
                        <Iconify icon="solar:trash-bin-trash-bold" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog para adicionar data especial */}
      <SpecialClosedDateForm
        open={closedDateDialog.value}
        onClose={closedDateDialog.onFalse}
        onSuccess={handleClosedDateCreated}
      />

      {/* Dialog de confirmação de eliminação */}
      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title="Eliminar Data Especial"
        content="Tem a certeza que deseja eliminar esta data especial de encerramento?"
        action={
          <Button variant="contained" color="error" onClick={handleDeleteClosedDate}>
            Eliminar
          </Button>
        }
      />
    </Container>
  );
}
